import { DEFAULT_LANE_SPAN_M, STATION_HALF_SIZE } from "./constants";
import { laneYMeters, snapToLaneY } from "./tunnelUtils";

/**
 * Pure, framework-free coordinate projection for the indoor-positioning
 * canvas, and the ONE place where the metres frame meets the screen.
 *
 * THE FRAME. Every stored and computed position is metres with the origin at
 * the floor plan's BOTTOM-LEFT and y pointing UP. DOM, SVG and canvas are
 * y-down by specification, so this module is the only place y (and, via
 * `screenRotationDeg`, an angle) is flipped:
 *   pixel.x = x * scaleX * zoom + pan.x
 *   pixel.y = (H - y) * scaleY * zoom + pan.y
 * `pan` is a plain pixel offset applied in screen space AFTER scale and zoom.
 * y = H lands on `pan.y` and y = 0 on `pan.y + H*scaleY*zoom`, so the floor
 * box is `floorRect` = [pan.y, pan.y + H*scaleY*zoom] and pinning the box's
 * top-left (the pan clamp) is unchanged by the flip.
 *
 * WHICH CALL TO USE.
 *   - geometry ON the floor (floor rect, tripwires, polygons, zone discs):
 *     `project` / `projectPoints`.
 *   - things placed IN a lane (stations, personnel, symbols, equipment tags,
 *     zone centres): `place`. On a plane layout `place` equals `project`.
 *   - converting a click/drag pixel back to metres: `unproject` (a point) or
 *     `pick` (a point snapped to the layout); `unprojectDelta` for a drag
 *     vector, which carries no origin and so no pan.
 *
 * NO GLYPH OFFSET. `project` returns the TRUE projected position. A glyph
 * positioned by `translate(x, y)` (top-left semantics) must subtract its OWN
 * half size (STATION_HALF_SIZE 6 for a 12px station, SYMBOL_HALF_SIZE 12 for
 * a 24px symbol); a glyph centred via `translate(-50%, -50%)` subtracts
 * nothing; converting a drag pixel back to metres adds the half size first.
 * Baking one glyph's half size into the projection once cost PersonnelMarker
 * about 10 m per axis on a large site (1.7 m per pixel).
 *
 * LAYOUTS. plane scales both axes by one uniform factor. line and tunnel
 * scale x against the width and y against the height independently, and the
 * effective height is `height > 0 ? height : DEFAULT_LANE_SPAN_M`, so a line
 * site with no declared height is still a valid viewport. line snaps y to
 * H/2; tunnel snaps y to the nearer of two lanes (see tunnelUtils).
 *
 * POSITIONS ARE PROJECTED AT RENDER TIME. State holds metres only; nothing
 * stores pixels, so a resize, zoom or pan is a plain re-render.
 *
 * READINESS SIGNAL: `pxPerMeter` is NOT one. On degenerate input (floor plan
 * not loaded: missing or zero dimensions or canvas) the scale falls back to
 * 1, so `pxPerMeter` is 1, which is truthy and wrong. Guard rendering on
 * `isValid`, true only when the scale came from real dimensions AND a real
 * canvas size.
 */

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const isPositive = (value) => typeof value === "number" && value > 0;

const isLaneLayout = (layoutType) =>
  layoutType === "line" || layoutType === "tunnel";

/** Floor height used for all maths; lane layouts fall back to a default span. */
const effectiveHeight = (dimensions, layoutType) => {
  if (isPositive(dimensions?.height)) return dimensions.height;
  return isLaneLayout(layoutType) ? DEFAULT_LANE_SPAN_M : 0;
};

/**
 * Scale from metres to canvas pixels. Plane uses one uniform factor
 * (min of the two axes); line/tunnel scale each axis independently and need
 * only a width and a canvas (the height falls back, see `effectiveHeight`).
 */
export const calculateScale = (dimensions, canvasSize, layoutType) => {
  const height = effectiveHeight(dimensions, layoutType);
  const dimsValid = isPositive(dimensions?.width) && height > 0;
  const canvasValid =
    isPositive(canvasSize?.width) && isPositive(canvasSize?.height);

  if (!dimsValid || !canvasValid) {
    return { scaleX: 1, scaleY: 1, scale: 1, isValid: false };
  }

  const scaleX = canvasSize.width / dimensions.width;
  const scaleY = canvasSize.height / height;

  if (isLaneLayout(layoutType)) {
    return { scaleX, scaleY, scale: scaleX, isValid: true };
  }

  const scale = Math.min(scaleX, scaleY);
  return { scaleX: scale, scaleY: scale, scale, isValid: true };
};

/** The only angle flip: metres-frame CCW degrees to a CSS/SVG rotate(). */
export const screenRotationDeg = (ccwDeg) => -ccwDeg;

const makeSnap = (layoutType, width, height) => (meters) => {
  const mx = meters?.x;
  const my = meters?.y;
  if (layoutType === "line") {
    return { x: clamp(mx ?? width / 2, 0, width), y: height / 2 };
  }
  if (layoutType === "tunnel") {
    return {
      x: clamp(mx ?? width / 2, 0, width),
      y: snapToLaneY(my ?? laneYMeters("upper", height), height),
    };
  }
  return { x: mx ?? 0, y: my ?? 0 };
};

/**
 * Builds a projection for one render of the canvas. All returned functions
 * are pure and close over the given dimensions/canvasSize/layoutType/zoom/pan.
 *
 * @param {object} params
 * @param {{width:number,height:number}} params.dimensions - floor size, metres
 * @param {{width:number,height:number}} params.canvasSize - canvas size, px
 * @param {"plane"|"line"|"tunnel"} [params.layoutType="plane"]
 * @param {number} [params.zoom=1]
 * @param {{x:number,y:number}} [params.pan={x:0,y:0}]
 * @returns {object} see the module header; `isValid` is the readiness signal.
 */
export const createViewport = ({
  dimensions,
  canvasSize,
  layoutType = "plane",
  zoom = 1,
  pan = { x: 0, y: 0 },
}) => {
  const { scaleX, scaleY, scale, isValid } = calculateScale(
    dimensions,
    canvasSize,
    layoutType,
  );
  const width = isPositive(dimensions?.width) ? dimensions.width : 0;
  const height = effectiveHeight(dimensions, layoutType);
  const sx = scaleX * zoom;
  const sy = scaleY * zoom;

  const project = (meters) => ({
    x: (meters?.x ?? 0) * sx + pan.x,
    y: (height - (meters?.y ?? 0)) * sy + pan.y,
  });

  const unproject = (pixels) => {
    if (sx === 0 || sy === 0) return { x: 0, y: 0 };
    return {
      x: ((pixels?.x ?? 0) - pan.x) / sx,
      y: height - ((pixels?.y ?? 0) - pan.y) / sy,
    };
  };

  const unprojectDelta = (delta) => {
    if (sx === 0 || sy === 0) return { x: 0, y: 0 };
    return { x: (delta?.x ?? 0) / sx, y: -(delta?.y ?? 0) / sy };
  };

  const projectPoints = (points) =>
    points.map(([x, y]) => {
      const p = project({ x, y });
      return [p.x, p.y];
    });

  const snap = makeSnap(layoutType, width, height);

  return {
    project,
    unproject,
    unprojectDelta,
    projectPoints,
    snap,
    place: (meters) => project(snap(meters)),
    pick: (pixels) => snap(unproject(pixels)),
    laneYPx: (lane) => project({ x: 0, y: laneYMeters(lane, height) }).y,
    floorRect: {
      left: pan.x,
      top: pan.y,
      width: width * sx,
      height: height * sy,
    },
    pxPerMeter: scale * zoom,
    scaleX,
    scaleY,
    zoom,
    pan,
    isValid,
    layoutType,
  };
};

/**
 * New `pan` so that, after changing zoom to `newZoom`, the metre coordinate
 * currently under `point` (a screen pixel) stays under that same point:
 * "zoom at cursor". Unprojects at the old zoom/pan, projects at the new zoom
 * with zero pan, and returns the difference. Callers feed it to the next
 * `createViewport`.
 */
export const zoomAtPoint = ({
  point,
  dimensions,
  canvasSize,
  layoutType,
  zoom,
  pan,
  newZoom,
}) => {
  const base = { dimensions, canvasSize, layoutType };
  const meters = createViewport({ ...base, zoom, pan }).unproject(point);
  const projected = createViewport({
    ...base,
    zoom: newZoom,
    pan: { x: 0, y: 0 },
  }).project(meters);
  return { x: point.x - projected.x, y: point.y - projected.y };
};

export { STATION_HALF_SIZE };
